// A client's half of /api/sync: what it remembers between syncs, and how one sync applies the server's answer
// to the local copy. Storage and transport are the platform's; the rules are here so every client agrees on
// them. See docs/client-synced-database.md.

import { comparePlayVersions, type SyncedPlay } from './syncedPlay';

/** Why the server refused a pushed play. Every reason but 'error' means the play should be dropped. */
export type SyncRejectReason = 'invalid' | 'board' | 'gone' | 'epoch' | 'error';

export interface SyncRequest {
  /** The highest seq the client has pulled; 0 asks for everything. */
  cursor: number;
  /** The epoch the client's copy belongs to; null if it has never synced. */
  epoch: number | null;
  push: SyncedPlay[];
}

export interface SyncResponse {
  epoch: number;
  cursor: number;
  /** The client's copy is from an older epoch: it discards it and keeps only `plays`. */
  reset: boolean;
  /** Everything changed since the cursor, plus the server's version of every play pushed. */
  plays: SyncedPlay[];
  rejected: { cid: string; reason: SyncRejectReason }[];
  /** How many plays the scope holds, for a client to check its own count against. */
  total: number;
}

/** Most plays one request may push. */
export const MAX_SYNC_PUSH = 500;

/** How often a client pulls everything rather than just what changed, to catch anything that slipped by. */
export const FULL_PULL_INTERVAL_MS = 30 * 24 * 60 * 60 * 1000;

export interface SyncMeta {
  cursor: number;
  epoch: number;
  /** Epoch ms of the last full pull; 0 asks for one at the next sync. */
  lastFullPullAt: number;
  /** Epoch ms of the last sync that completed, and how many plays the server said the scope held then.
   * For showing the sync's health; nothing decides by them. Absent on a scope last synced before they
   * were recorded. */
  lastSyncAt?: number;
  serverTotal?: number;
}

/**
 * One scope's local plays, as the sync needs them. Calls are synchronous, as a local database answers
 * them; `transaction` makes the application of one response all-or-nothing.
 */
export interface SyncStore {
  readMeta(): SyncMeta | null;
  writeMeta(meta: SyncMeta): void;
  get(cid: string): (SyncedPlay & { dirty: boolean }) | null;
  put(play: SyncedPlay, dirty: boolean): void;
  remove(cid: string): void;
  /** Plays waiting to be pushed, oldest change first. */
  dirty(limit: number): SyncedPlay[];
  /** The plays the server has acknowledged (those with a seq), with whether each is dirty. */
  acknowledged(): { cid: string; dirty: boolean }[];
  /** Drops every play and the meta. */
  clear(): void;
  transaction(fn: () => void): void;
}

export type SyncTransport = (request: SyncRequest) => Promise<SyncResponse>;

export interface SyncOutcome {
  /** Whether anything in the store changed, for whoever draws from it. */
  changed: boolean;
  full: boolean;
  reset: boolean;
}

/** Pushes per sync before leaving the rest for next time, so a huge backlog can't hold one sync forever. */
const MAX_ROUNDS = 10;

/**
 * Pushes this scope's dirty plays and pulls what changed on the server, as many rounds as the backlog needs.
 * Throws if the server can't be reached, leaving the store as the last completed round left it.
 */
export async function syncScope(store: SyncStore, send: SyncTransport, now = Date.now()): Promise<SyncOutcome> {
  const outcome: SyncOutcome = { changed: false, full: false, reset: false };

  for (let round = 0; round < MAX_ROUNDS; round++) {
    const meta = store.readMeta();
    const full = !meta || meta.cursor === 0 || now - meta.lastFullPullAt > FULL_PULL_INTERVAL_MS;
    const push = store.dirty(MAX_SYNC_PUSH);

    const response = await send({ cursor: full ? 0 : meta.cursor, epoch: meta?.epoch ?? null, push });
    store.transaction(() => applyResponse(store, response, full, now));

    outcome.changed ||= response.plays.length > 0 || response.rejected.length > 0 || response.reset;
    outcome.full ||= full || response.reset;
    outcome.reset ||= response.reset;

    if (push.length < MAX_SYNC_PUSH) break;
  }
  return outcome;
}

function applyResponse(store: SyncStore, response: SyncResponse, full: boolean, now: number) {
  if (response.reset) store.clear();

  for (const server of response.plays) {
    const local = store.get(server.cid);
    if (!local || comparePlayVersions(server, local) >= 0) {
      store.put(server, false);
    } else {
      // Ours is newer: the server has an older version, which makes ours its successor, still to be sent.
      store.put({ ...stripDirty(local), seq: server.seq }, true);
    }
  }

  for (const { cid, reason } of response.rejected) {
    if (reason !== 'error' && cid) store.remove(cid);
  }

  const fullPull = full || response.reset;
  if (fullPull) {
    // The server deleted what it acknowledged once and no longer has: a deleted board, a stale duplicate.
    const held = new Set(response.plays.map((play) => play.cid));
    for (const { cid, dirty } of store.acknowledged()) {
      if (!dirty && !held.has(cid)) store.remove(cid);
    }
  }

  // A count that disagrees means something slipped past the cursor; the next sync pulls everything.
  const countsAgree = fullPull || store.acknowledged().length === response.total;
  const previous = store.readMeta();
  store.writeMeta({
    cursor: response.cursor,
    epoch: response.epoch,
    lastFullPullAt: fullPull ? now : countsAgree ? (previous?.lastFullPullAt ?? 0) : 0,
    lastSyncAt: now,
    serverTotal: response.total,
  });
}

function stripDirty(play: SyncedPlay & { dirty?: boolean }): SyncedPlay {
  const { dirty: _dirty, ...rest } = play;
  return rest;
}

/** How one scope's copy stands, for a debug readout of the sync's health. */
export interface SyncStatus {
  scope: string;
  /** Plays held, and how many of them are finished. */
  plays: number;
  complete: number;
  /** Plays changed here and not yet pushed, and when the oldest of those changes was made. */
  unsent: number;
  oldestUnsentAt: number | null;
  /** Plays the server has acknowledged, which is what `meta.serverTotal` should equal. */
  acknowledged: number;
  /** Plays kept on this device on purpose and never sent: unfinished tutorials. */
  localOnly: number;
  meta: SyncMeta | null;
  /** Every scope stored on this device, with its play count. */
  scopes: { scope: string; plays: number }[];
}
